import { Checkbox, DatePicker, Flex, Space, Switch, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { FC, useEffect } from 'react';
import { get, useFormContext, useWatch } from 'react-hook-form';

import { FormItem } from '../FormItem';
import { Section } from '../Section';

const { Text } = Typography;

function disabledDate(current: Dayjs): boolean {
  return current && current.isBefore(dayjs().startOf('day'));
}

function disabledTime(date: Dayjs | null): {
  disabledHours?: () => number[];
  disabledMinutes?: (selectedHour: number) => number[];
} {
  if (!date || !date.isSame(dayjs(), 'day')) return {};

  const now = dayjs();
  const currentHour = now.hour();
  const currentMinute = now.minute();

  return {
    disabledHours: () => Array.from({ length: currentHour }, (_, i) => i),
    disabledMinutes: (selectedHour: number) =>
      selectedHour === currentHour
        ? Array.from({ length: currentMinute + 1 }, (_, i) => i)
        : [],
  };
}

export const BannerScheduleStep: FC = () => {
  const {
    control,
    formState: { errors },
    clearErrors,
    getValues,
    setValue,
  } = useFormContext();

  const withoutEndDate = useWatch({
    control,
    name: 'schedule.withoutEndDate',
  });

  useEffect(() => {
    const currentPublishType = getValues('schedule.publishType');
    const currentIsActive = getValues('schedule.isActive');

    if (!currentPublishType) {
      setValue('schedule.publishType', 'scheduled', { shouldDirty: false });
    }

    if (typeof currentIsActive !== 'boolean') {
      setValue('schedule.isActive', false, { shouldDirty: false });
    }
  }, [getValues, setValue]);

  useEffect(() => {
    if (withoutEndDate) {
      setValue('schedule.scheduledTime', null);
      clearErrors('schedule.scheduledTime');
    }
  }, [withoutEndDate, clearErrors, setValue]);

  return (
    <Space direction='vertical' size={32} style={{ width: '100%' }}>
      <Section
        title='Publicação'
        subtitle='Defina quando e como este banner será publicado'
        type='primary'
      >
        <Flex align='center' gap={12}>
          <Text style={{ minWidth: 'fit-content', fontSize: 12 }}>Status</Text>
          <FormItem
            control={control}
            name='schedule.isActive'
            renderItem={({ field }) => (
              <Flex align='center' gap={12}>
                <Switch checked={!!field.value} onChange={field.onChange} />
                <Text style={{ fontSize: 12 }}>
                  {field.value ? 'Ativo' : 'Desativado'}
                </Text>
              </Flex>
            )}
          />
        </Flex>
        <Flex gap={16} style={{ maxWidth: '50%' }}>
          <Flex vertical style={{ flex: 1, minWidth: 0 }}>
            <FormItem
              label='Data de início'
              control={control}
              name='schedule.scheduledDate'
              renderItem={({ field }) => (
                <DatePicker
                  {...field}
                  showTime={{ format: 'HH:mm' }}
                  placeholder='Selecione data e hora'
                  style={{ width: '100%' }}
                  format='DD/MM/YYYY HH:mm'
                  disabledDate={disabledDate}
                  disabledTime={disabledTime}
                  status={
                    get(errors, 'schedule.scheduledDate') ? 'error' : undefined
                  }
                />
              )}
            />
          </Flex>

          <Flex vertical style={{ flex: 1 }} gap={8}>
            <FormItem
              label='Data de término'
              control={control}
              name='schedule.scheduledTime'
              renderItem={({ field }) => (
                <DatePicker
                  {...field}
                  showTime={{ format: 'HH:mm' }}
                  disabled={!!withoutEndDate}
                  placeholder='Selecione data e hora'
                  style={{ width: '100%' }}
                  format='DD/MM/YYYY HH:mm'
                  disabledDate={disabledDate}
                  disabledTime={disabledTime}
                  status={
                    !withoutEndDate && get(errors, 'schedule.scheduledTime')
                      ? 'error'
                      : undefined
                  }
                />
              )}
            />

            <FormItem
              control={control}
              name='schedule.withoutEndDate'
              renderItem={({ field }) => (
                <Checkbox
                  checked={!!field.value}
                  onChange={event => field.onChange(event.target.checked)}
                >
                  Sem data fim
                </Checkbox>
              )}
            />
          </Flex>
        </Flex>
      </Section>
    </Space>
  );
};
